/**
 * Cancellation Stats Report — pro Lehrkraft kumulierte Ausfälle pro Jahr.
 * Zeigt Anzahl, Erstattungssumme und Ø-Quotient. Klick auf Lehrkraft
 * filtert nach. Nutzt /cancellations/stats endpoint.
 */
import React, { useEffect, useState } from 'react';
import { Loader2, AlertTriangle } from 'lucide-react';
import api from '@/lib/axios';
import Layout from '@/components/layout/Layout';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { formatDeNumber } from '@/lib/utils';

export default function CancellationStatsPage() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/cancellations/stats?year=${year}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [year]);

  return (
    <Layout>
      <div className="p-6 md:p-8 max-w-5xl mx-auto" data-testid="cancellation-stats-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <AlertTriangle className="w-6 h-6 text-brand-warning" />
              Ausfall-Statistik · {year}
            </h1>
            <p className="text-sm text-slate-500">
              Anzahl, Erstattungssumme und Ø-Quotient pro Lehrkraft
            </p>
          </div>
          <div className="flex items-center gap-1">
            <Button size="sm" variant="ghost" onClick={() => setYear(year - 1)} className="h-8 w-8 p-0" data-testid="year-prev">‹</Button>
            <span className="font-mono text-sm w-14 text-center font-semibold">{year}</span>
            <Button size="sm" variant="ghost" onClick={() => setYear(year + 1)} className="h-8 w-8 p-0" data-testid="year-next">›</Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : data ? (
          <>
            {/* Summary cards */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              <Card className="p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500">Σ Ausfälle</div>
                <div className="text-3xl font-bold text-slate-800">{data.total}</div>
              </Card>
              <Card className="p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500">Σ erstattet</div>
                <div className="text-3xl font-bold text-brand-red">
                  {formatDeNumber(data.refunded_total)} €
                </div>
              </Card>
              <Card className="p-4">
                <div className="text-xs uppercase tracking-wider text-slate-500">Lehrkräfte mit Ausfällen</div>
                <div className="text-3xl font-bold text-slate-800">{data.items.length}</div>
              </Card>
            </div>

            {/* Per-teacher table */}
            <Card className="overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[11px]">
                  <tr>
                    <th className="text-left py-2 px-3">Lehrkraft</th>
                    <th className="text-right py-2 px-3">Anzahl</th>
                    <th className="text-right py-2 px-3">Erstattet</th>
                    <th className="text-right py-2 px-3">Ø Quotient</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.length === 0 ? (
                    <tr><td colSpan={4} className="py-12 text-center text-slate-400 italic">Keine Ausfälle in {year}</td></tr>
                  ) : data.items.map((it) => (
                    <tr
                      key={it.teacher_id}
                      className="border-t border-slate-100"
                      data-testid={`stats-row-${it.teacher_id}`}
                    >
                      <td className="py-2 px-3 font-medium">{it.teacher_name}</td>
                      <td className="py-2 px-3 text-right font-mono">{it.count}</td>
                      <td className="py-2 px-3 text-right font-mono font-semibold text-brand-red">
                        {formatDeNumber(it.refunded_total)} €
                      </td>
                      <td className="py-2 px-3 text-right font-mono text-slate-600">
                        {formatDeNumber(it.avg_quotient)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </>
        ) : (
          <p className="text-sm text-slate-400 italic text-center py-12">Keine Daten</p>
        )}
      </div>
    </Layout>
  );
}
